import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Edit, FileText, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const CompensationClaimsForm = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [view, setView] = useState('list');
  const [entries, setEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    exercise_name: currentExercise?.exercise_name || '',
    incident_name: '',
    operational_period_from: '',
    operational_period_to: '',
    unit_leader: '',
    contact_info: '',
    injury_fatality_items: [],
    property_damage_items: [],
    expense_items: [],
    total_estimated_cost: '',
    notes: '',
    prepared_by: '',
    date_prepared: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved', 'saving', 'error'
  const [lastSynced, setLastSynced] = useState(null);
  const saveTimeoutRef = React.useRef(null);

  // Auto-save function with debounce
  const autoSave = React.useCallback(async () => {
    if (!currentExercise?.id || !editingEntry) return;
    
    setSaving(true);
    setSaveStatus('saving');
    try {
      const payload = {
        ...formData,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.exercise_name
      };

      const response = await fetch(`${API}/cost-unit/${editingEntry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSaveStatus('saved');
        setLastSynced(new Date());
        // Clear saved status after 3 seconds
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [formData, currentExercise, editingEntry, API]);

  // Debounced auto-save when form data changes (only in edit mode)
  React.useEffect(() => {
    if (editingEntry && view === 'form') {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 2000); // 2 second debounce
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, editingEntry, view]);

  // Fetch entries
  const fetchEntries = async (silent = false) => {
    if (!currentExercise?.id) return;
    
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${API}/compensation-claims?exercise_id=${currentExercise.id}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
        setLastSynced(new Date());
      }
    } catch (error) {
      if (!silent) console.error('Error fetching Compensation and Claims entries:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentExercise?.id]);

  // Setup polling for real-time sync (every 30 seconds)
  useEffect(() => {
    if (currentExercise?.id) {
      const pollInterval = setInterval(() => {
        fetchEntries(true);
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [currentExercise?.id]);

  // Initialize form with editing data
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        exercise_name: editingEntry.exercise_name || currentExercise?.exercise_name || '',
        incident_name: editingEntry.incident_name || '',
        operational_period_from: editingEntry.operational_period_from || '',
        operational_period_to: editingEntry.operational_period_to || '',
        unit_leader: editingEntry.unit_leader || '',
        contact_info: editingEntry.contact_info || '',
        injury_fatality_items: editingEntry.injury_fatality_items || [],
        property_damage_items: editingEntry.property_damage_items || [],
        expense_items: editingEntry.expense_items || [],
        total_estimated_cost: editingEntry.total_estimated_cost || '',
        notes: editingEntry.notes || '',
        prepared_by: editingEntry.prepared_by || '',
        date_prepared: editingEntry.date_prepared || ''
      });
    } else {
      setFormData({
        exercise_name: currentExercise?.exercise_name || '',
        incident_name: '',
        operational_period_from: '',
        operational_period_to: '',
        unit_leader: '',
        contact_info: '',
        injury_fatality_items: [],
        property_damage_items: [],
        expense_items: [],
        total_estimated_cost: '',
        notes: '',
        prepared_by: '',
        date_prepared: ''
      });
    }
  }, [editingEntry, currentExercise]);

  // Add injury/fatality item
  const addInjuryFatalityItem = () => {
    const newItem = {
      id: Date.now().toString(),
      date_time: '',
      incident_type: 'Injury',
      person_name: '',
      description: '',
      estimated_cost: '',
      status: 'Pending'
    };
    setFormData(prev => ({
      ...prev,
      injury_fatality_items: [...prev.injury_fatality_items, newItem]
    }));
  };

  // Update injury/fatality item
  const updateInjuryFatalityItem = (index, field, value) => {
    const updated = [...formData.injury_fatality_items];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, injury_fatality_items: updated }));
  };

  // Delete injury/fatality item
  const deleteInjuryFatalityItem = (index) => {
    setFormData(prev => ({
      ...prev,
      injury_fatality_items: prev.injury_fatality_items.filter((_, i) => i !== index)
    }));
  };

  // Add property damage item
  const addPropertyDamageItem = () => {
    const newItem = {
      id: Date.now().toString(),
      date_time: '',
      property_type: '',
      owner: '',
      description: '',
      estimated_cost: '',
      status: 'Reported'
    };
    setFormData(prev => ({
      ...prev,
      property_damage_items: [...prev.property_damage_items, newItem]
    }));
  };

  // Update property damage item
  const updatePropertyDamageItem = (index, field, value) => {
    const updated = [...formData.property_damage_items];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, property_damage_items: updated }));
  };

  // Delete property damage item
  const deletePropertyDamageItem = (index) => {
    setFormData(prev => ({
      ...prev,
      property_damage_items: prev.property_damage_items.filter((_, i) => i !== index)
    }));
  };

  // Add expense item
  const addExpenseItem = () => {
    const newItem = {
      id: Date.now().toString(),
      date: '',
      category: '',
      description: '',
      amount: '',
      approved_by: ''
    };
    setFormData(prev => ({
      ...prev,
      expense_items: [...prev.expense_items, newItem]
    }));
  };

  // Update expense item
  const updateExpenseItem = (index, field, value) => {
    const updated = [...formData.expense_items];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(prev => ({ ...prev, expense_items: updated }));
  };

  // Delete expense item
  const deleteExpenseItem = (index) => {
    setFormData(prev => ({
      ...prev,
      expense_items: prev.expense_items.filter((_, i) => i !== index)
    }));
  };

  // Save entry
  const saveEntry = async () => {
    if (!currentExercise?.id) {
      alert('No exercise selected');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        exercise_id: currentExercise.id,
        exercise_name: currentExercise.exercise_name
      };

      let response;
      if (editingEntry) {
        response = await fetch(`${API}/compensation-claims/${editingEntry.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API}/compensation-claims`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        alert(editingEntry ? 'Entry updated successfully!' : 'Entry created successfully!');
        fetchEntries();
        setView('list');
        setEditingEntry(null);
      } else {
        alert('Failed to save entry');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Error saving entry');
    } finally {
      setLoading(false);
    }
  };

  // Delete entry
  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/compensation-claims/${entryId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Entry deleted successfully!');
        fetchEntries();
      } else {
        alert('Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Error deleting entry');
    } finally {
      setLoading(false);
    }
  };

  // Comprehensive Print Function (DOM-safe via writeSanitizedHTML)
  const handlePrint = () => {
    const data = formData || {};
    const exerciseName = data.exercise_name || currentExercise?.exercise_name || 'N/A';
    const incidentName = data.incident_name || 'N/A';
    const generated = new Date().toLocaleString();

    const tableSection = (title, columns, rows, emptyMsg) => {
      if (!rows || rows.length === 0) {
        return `<div class="section"><h2>${title}</h2><p class="empty">${emptyMsg}</p></div>`;
      }
      const head = columns.map(c => `<th>${c.label}</th>`).join('');
      const body = rows.map(r =>
        `<tr>${columns.map(c => `<td>${r[c.key] != null && r[c.key] !== '' ? r[c.key] : ''}</td>`).join('')}</tr>`
      ).join('');
      return `
        <div class="section">
          <h2>${title} <span class="count">(${rows.length})</span></h2>
          <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
        </div>`;
    };

    const infoItem = (label, value) =>
      `<div class="info-item"><div class="info-label">${label}</div><div class="info-value">${value || 'N/A'}</div></div>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Compensation and Claims Report - ${exerciseName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; color: #111827; }
            h1 { color: #3b82f6; text-align: center; border-bottom: 3px solid #3b82f6; padding-bottom: 10px; margin-bottom: 6px; }
            .sub { text-align: center; color: #6b7280; margin-bottom: 24px; font-size: 12px; }
            h2 { color: #3b82f6; margin-top: 26px; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; font-size: 16px; }
            .count { color: #6b7280; font-size: 13px; font-weight: normal; }
            .section { margin-bottom: 22px; page-break-inside: avoid; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 10px 0; }
            .info-grid-1 { display: grid; grid-template-columns: 1fr; gap: 10px; margin: 10px 0; }
            .info-item { padding: 8px 10px; background: #f9fafb; border-left: 3px solid #3b82f6; }
            .info-label { font-weight: bold; color: #6b7280; font-size: 11px; text-transform: uppercase; }
            .info-value { color: #111827; margin-top: 3px; font-size: 13px; white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; vertical-align: top; }
            th { background: #eff6ff; font-weight: bold; color: #1e40af; }
            tr:nth-child(even) { background: #f9fafb; }
            .empty { color: #9ca3af; font-style: italic; font-size: 12px; }
            .footer { text-align: center; color: #6b7280; margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; }
            @media print { body { padding: 12px; } .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Compensation and Claims Report</h1>
          <div class="sub"><strong>Exercise:</strong> ${exerciseName} &nbsp;•&nbsp; <strong>Incident:</strong> ${incidentName} &nbsp;•&nbsp; <strong>Generated:</strong> ${generated}</div>

          <div class="section">
            <h2>Basic Information</h2>
            <div class="info-grid">
              ${infoItem('Exercise Name', exerciseName)}
              ${infoItem('Incident Name', incidentName)}
              ${infoItem('Operational Period From', data.operational_period_from)}
              ${infoItem('Operational Period To', data.operational_period_to)}
              ${infoItem('Unit Leader', data.unit_leader)}
              ${infoItem('Contact Info', data.contact_info)}
            </div>
          </div>

          ${tableSection('Injuries and Fatalities',
            [
              { key: 'date_time', label: 'Date/Time' },
              { key: 'incident_type', label: 'Type' },
              { key: 'person_name', label: 'Person' },
              { key: 'description', label: 'Description' },
              { key: 'estimated_cost', label: 'Est. Cost ($)' },
              { key: 'status', label: 'Status' }
            ],
            data.injury_fatality_items,
            'No injury/fatality records.')}

          ${tableSection('Property Damage',
            [
              { key: 'date_time', label: 'Date/Time' },
              { key: 'property_type', label: 'Type' },
              { key: 'owner', label: 'Owner' },
              { key: 'description', label: 'Description' },
              { key: 'estimated_cost', label: 'Est. Cost ($)' },
              { key: 'status', label: 'Status' }
            ],
            data.property_damage_items,
            'No property damage records.')}

          ${tableSection('Cost and Expenses',
            [
              { key: 'date', label: 'Date' },
              { key: 'category', label: 'Category' },
              { key: 'description', label: 'Description' },
              { key: 'amount', label: 'Amount ($)' },
              { key: 'approved_by', label: 'Approved By' }
            ],
            data.expense_items,
            'No expense records.')}

          <div class="section">
            <h2>Summary</h2>
            <div class="info-grid">
              ${infoItem('Total Estimated Cost ($)', data.total_estimated_cost)}
              ${infoItem('Date Prepared', data.date_prepared)}
            </div>
            <div class="info-grid-1">
              ${infoItem('Notes', data.notes)}
              ${infoItem('Prepared By', data.prepared_by)}
            </div>
          </div>

          <div class="footer">
            <div><strong>Compensation and Claims Report</strong> &nbsp;|&nbsp; ${exerciseName}</div>
            <div>Powered by EXRSIM</div>
          </div>
        </body>
      </html>
    `;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  // List view
  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Compensation and Claims</h1>
            <p className={`${theme.colors.textMuted}`}>Manage financial and administrative data for property damage, injuries, and fatalities</p>
          </div>
          <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className={`${theme.colors.textMuted}`}>Loading...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <FileText className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <p className={`${theme.colors.textMuted} mb-4`}>No entries found</p>
            <Button onClick={() => setView('form')} className="bg-orange-500 hover:bg-orange-600">
              Create First Entry
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card key={entry.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardHeader>
                  <CardTitle className={`${theme.colors.textPrimary}`}>
                    {entry.incident_name || 'Untitled Entry'}
                  </CardTitle>
                  <CardDescription className={`${theme.colors.textMuted}`}>
                    Period: {entry.operational_period_from} - {entry.operational_period_to} | Total Cost: ${entry.total_estimated_cost || '0'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingEntry(entry);
                        setView('form');
                      }}
                      className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteEntry(entry.id)}
                      className="border-red-500 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Form view
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex justify-between items-center print:hidden">
        <Button onClick={() => {
          setView('list');
          setEditingEntry(null);
        }} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button onClick={handlePrint} className="bg-blue-500 hover:bg-blue-600">
          <Printer className="h-4 w-4 mr-2" />
          Print Report
        </Button>
      </div>

      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Compensation and Claims Report</h1>
        </div>

        {/* Basic Information Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Exercise Name</Label>
                <div className={`p-2 bg-gray-800 rounded border border-gray-600`}>
                  <span className={`${theme.colors.textPrimary}`}>{currentExercise?.exercise_name || 'N/A'}</span>
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Incident Name</Label>
                <Input
                  value={formData.incident_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_name: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period From</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_from ? formData.operational_period_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_from ? formData.operational_period_from.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_from: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_from ? formData.operational_period_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_from ? formData.operational_period_from.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_from: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Operational Period To</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={formData.operational_period_to ? formData.operational_period_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.operational_period_to ? formData.operational_period_to.split('T')[1] : '00:00';
                      setFormData(prev => ({ ...prev, operational_period_to: `${date}T${time}` }));
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.operational_period_to ? formData.operational_period_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.operational_period_to ? formData.operational_period_to.split('T')[0] : '';
                        setFormData(prev => ({ ...prev, operational_period_to: `${date}T${value}` }));
                      }
                    }}
                    className={`bg-gray-800 border-gray-600 text-white`}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Unit Leader</Label>
                <Input
                  value={formData.unit_leader}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit_leader: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Contact Information</Label>
                <Input
                  value={formData.contact_info}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_info: e.target.value }))}
                  placeholder="Email or Phone"
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Injuries and Fatalities Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Injuries and Fatalities</CardTitle>
            <CardDescription className={`${theme.colors.textMuted}`}>
              Track injuries and fatalities requiring compensation claims
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Date/Time</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Type</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Person Name</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Description</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Est. Cost ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Status</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.injury_fatality_items.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <div className="grid grid-cols-2 gap-1">
                          <Input
                            type="date"
                            value={item.date_time ? item.date_time.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = item.date_time ? item.date_time.split('T')[1] : '00:00';
                              updateInjuryFatalityItem(index, 'date_time', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={item.date_time ? item.date_time.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = item.date_time ? item.date_time.split('T')[0] : '';
                                updateInjuryFatalityItem(index, 'date_time', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </td>
                      <td className="p-2">
                        <Select value={item.incident_type} onValueChange={(value) => updateInjuryFatalityItem(index, 'incident_type', value)}>
                          <SelectTrigger className={`bg-gray-800 border-gray-600 text-white w-32`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Injury">Injury</SelectItem>
                            <SelectItem value="Fatality">Fatality</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.person_name}
                          onChange={(e) => updateInjuryFatalityItem(index, 'person_name', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.description}
                          onChange={(e) => updateInjuryFatalityItem(index, 'description', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[200px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.estimated_cost}
                          onChange={(e) => updateInjuryFatalityItem(index, 'estimated_cost', e.target.value)}
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-32`}
                        />
                      </td>
                      <td className="p-2">
                        <Select value={item.status} onValueChange={(value) => updateInjuryFatalityItem(index, 'status', value)}>
                          <SelectTrigger className={`bg-gray-800 border-gray-600 text-white w-32`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Processing">Processing</SelectItem>
                            <SelectItem value="Approved">Approved</SelectItem>
                            <SelectItem value="Denied">Denied</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteInjuryFatalityItem(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addInjuryFatalityItem} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Injury/Fatality
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Property Damage Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Property Damage</CardTitle>
            <CardDescription className={`${theme.colors.textMuted}`}>
              Document property damage requiring compensation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Date/Time</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Property Type</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Owner</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Description</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Est. Cost ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Status</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.property_damage_items.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <div className="grid grid-cols-2 gap-1">
                          <Input
                            type="date"
                            value={item.date_time ? item.date_time.split('T')[0] : ''}
                            onChange={(e) => {
                              const date = e.target.value;
                              const time = item.date_time ? item.date_time.split('T')[1] : '00:00';
                              updatePropertyDamageItem(index, 'date_time', `${date}T${time}`);
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            pattern="[0-2][0-9]:[0-5][0-9]"
                            maxLength="5"
                            value={item.date_time ? item.date_time.split('T')[1] : ''}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9:]/g, '');
                              if (value.length === 2 && !value.includes(':')) {
                                value = value + ':';
                              }
                              if (value.length <= 5) {
                                const date = item.date_time ? item.date_time.split('T')[0] : '';
                                updatePropertyDamageItem(index, 'date_time', `${date}T${value}`);
                              }
                            }}
                            className={`bg-gray-800 border-gray-600 text-white`}
                          />
                        </div>
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.property_type}
                          onChange={(e) => updatePropertyDamageItem(index, 'property_type', e.target.value)}
                          placeholder="Building, Vehicle, etc."
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.owner}
                          onChange={(e) => updatePropertyDamageItem(index, 'owner', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.description}
                          onChange={(e) => updatePropertyDamageItem(index, 'description', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[200px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.estimated_cost}
                          onChange={(e) => updatePropertyDamageItem(index, 'estimated_cost', e.target.value)}
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-32`}
                        />
                      </td>
                      <td className="p-2">
                        <Select value={item.status} onValueChange={(value) => updatePropertyDamageItem(index, 'status', value)}>
                          <SelectTrigger className={`bg-gray-800 border-gray-600 text-white w-32`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Reported">Reported</SelectItem>
                            <SelectItem value="Assessing">Assessing</SelectItem>
                            <SelectItem value="Approved">Approved</SelectItem>
                            <SelectItem value="Resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deletePropertyDamageItem(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addPropertyDamageItem} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Property Damage
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cost and Expenses Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Cost and Expenses</CardTitle>
            <CardDescription className={`${theme.colors.textMuted}`}>
              Track administrative costs and expenses
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className={`${theme.colors.border} border-b`}>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Date</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Category</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Description</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Amount ($)</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2`}>Approved By</th>
                    <th className={`${theme.colors.textSecondary} text-left p-2 print:hidden`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.expense_items.map((item, index) => (
                    <tr key={item.id} className={`${theme.colors.border} border-b`}>
                      <td className="p-2">
                        <Input
                          type="date"
                          value={item.date}
                          onChange={(e) => updateExpenseItem(index, 'date', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white w-40`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.category}
                          onChange={(e) => updateExpenseItem(index, 'category', e.target.value)}
                          placeholder="e.g., Medical, Legal"
                          className={`bg-gray-800 border-gray-600 text-white min-w-[120px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Textarea
                          value={item.description}
                          onChange={(e) => updateExpenseItem(index, 'description', e.target.value)}
                          rows={1}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[200px]`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.amount}
                          onChange={(e) => updateExpenseItem(index, 'amount', e.target.value)}
                          placeholder="0.00"
                          className={`bg-gray-800 border-gray-600 text-white w-32`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.approved_by}
                          onChange={(e) => updateExpenseItem(index, 'approved_by', e.target.value)}
                          className={`bg-gray-800 border-gray-600 text-white min-w-[150px]`}
                        />
                      </td>
                      <td className="p-2 print:hidden">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteExpenseItem(index)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 print:hidden">
              <Button onClick={addExpenseItem} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Card */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Total Estimated Cost ($)</Label>
                <Input
                  value={formData.total_estimated_cost}
                  onChange={(e) => setFormData(prev => ({ ...prev, total_estimated_cost: e.target.value }))}
                  placeholder="0.00"
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
              <div>
                <Label className={`text-white text-xs font-medium mb-1 block`}>Date Prepared</Label>
                <Input
                  type="date"
                  value={formData.date_prepared}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_prepared: e.target.value }))}
                  className={`bg-gray-800 border-gray-600 text-white`}
                />
              </div>
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Additional notes or comments..."
                className={`bg-gray-800 border-gray-600 text-white`}
              />
            </div>
            <div>
              <Label className={`text-white text-xs font-medium mb-1 block`}>Prepared By</Label>
              <Input
                value={formData.prepared_by}
                onChange={(e) => setFormData(prev => ({ ...prev, prepared_by: e.target.value }))}
                className={`bg-gray-800 border-gray-600 text-white`}
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end print:hidden">
          <Button onClick={saveEntry} disabled={loading} className="bg-orange-500 hover:bg-orange-600">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')} Entry
          </Button>
        </div>
      </div>
    </div>
  );
};

// Cost Unit Form

export default CompensationClaimsForm;
